import type { OverviewAnalysisResult, OverviewIssueDetail } from "../../modules/overviewAnalysis/types";
import type { AppLanguage } from "src/i18n";

const formatNumber = (value: number | undefined) => value === undefined
	? null
	: new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format(value);

const englishCountryNames = new Intl.DisplayNames(["en"], { type: "region", fallback: "none" });

const localizeSubjects = (detail: OverviewIssueDetail): OverviewIssueDetail => {
	if (detail.entity?.type !== "country") return detail;
	const code = detail.entity.value === "UK" ? "GB" : detail.entity.value;
	return { ...detail, entity: { ...detail.entity, label: englishCountryNames.of(code) || detail.entity.value } };
};

const subjectName = (detail: OverviewIssueDetail) => detail.entity?.label ?? detail.subject?.label;

const englishIssue = (detail: OverviewIssueDetail): string => {
	const evidence = detail.evidence;
	const subject = subjectName(detail);
	const attempts = formatNumber(evidence?.attempts ?? evidence?.current);
	const current = formatNumber(evidence?.currentRate);
	const previous = formatNumber(evidence?.previousRate ?? undefined);
	const volumeDrop = evidence?.ratio !== undefined ? formatNumber((1 - evidence.ratio) * 100) : null;

	if (detail.id === "insufficient") return "At least 30 payment attempts are required for a reliable assessment.";
	if (evidence?.kind === "email_declines") {
		return `${subject}: ${formatNumber(evidence.failures)} declines out of ${attempts} attempts (${formatNumber(evidence.failureRate)}%). Conversion is ${current}%. Review decline reasons and repeat payment attempts.`;
	}
	if (detail.id === "technical_errors") {
		const count = formatNumber(evidence?.count);
		return `Recurring technical errors were found in the overall statistics${count ? ` (${count})` : ""}.`;
	}
	if (detail.id.includes("volume")) {
		return `${volumeDrop ? `Payment attempts decreased by ${volumeDrop}%` : "Payment volume decreased"} compared with the expected level for the selected period.`;
	}
	if (detail.id.endsWith("_growth")) {
		return `${detail.id.startsWith("refunds") ? "Refunds" : "Chargebacks"} increased materially compared with the previous period.`;
	}
	if (current) {
		const movement = previous && Number(previous.replace(/,/g, "")) > Number(current.replace(/,/g, ""))
			? `conversion decreased from ${previous}% to ${current}%`
			: `conversion is ${current}%`;
		return `${subject ? `${subject}: ` : ""}${movement}${Number(current.replace(/,/g, "")) < 80 ? ", below the 80% target" : ""}${attempts ? ` (${attempts} attempts)` : ""}.`;
	}
	return subject ? `${subject}: requires attention.` : "The selected period requires attention.";
};

export const localizeOverviewAnalysis = (result: OverviewAnalysisResult, language: AppLanguage): OverviewAnalysisResult => {
	if (language === "ru") return result;

	const issueDetails = result.issues.map((text, index) => {
		const detail = result.issueDetails?.[index];
		const source = localizeSubjects(detail?.text === text ? detail : { id: `issue-${index}`, text });
		return { ...source, text: englishIssue(source) };
	});
	const observations = (result.observations ?? []).map(detail => {
		const localized = localizeSubjects(detail);
		return {
			...localized,
			text: subjectName(localized) ? `${subjectName(localized)}: an unusual concentration was found in the selected period.` : "An additional observation was found.",
		};
	});
	const summary = result.status === "ok"
		? `Successfully processed ${formatNumber(result.metrics.conversion ?? undefined) ?? "0"}% of payments.`
		: result.status === "insufficient_data"
			? "There is not enough data for a reliable assessment yet."
			: issueDetails[0]?.id === "technical_errors"
				? "Check the payment data transfer and integration settings."
				: "Review the decline reasons and payment provider performance.";

	return {
		...result,
		summary,
		issues: issueDetails.map(detail => detail.text),
		issueDetails,
		observations,
	};
};